import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    DataTable,
    Heading,
    Meter,
    Notification,
    Page,
    PageContent,
    Pagination,
    Tag,
    Text,
} from 'grommet';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router-dom';
import {
    API,
    AppConstants,
    SchoolService,
    Utils,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { SchoolManualReportButton } from './SchoolManualReportButton';
import { Trash } from 'grommet-icons';
import { AddSchoolReportRecipientButton } from './AddSchoolReportRecipientButton';
import { EditSchoolButton } from './EditSchoolButton';

export const SchoolProfile = () => {
    const school = useLoaderData();
    const revalidator = useRevalidator();
    const [notification, setNotification] = useState(null);

    const reportRecipientTableStepOptions = [5, 10];
    const reportRecipientsData =
        school.attributes.reportRecipients?.map((r, i) => ({
            id: i + 1,
            email: r,
        })) || [];

    const [reportRecipients, setReportRecipients] =
        useState(reportRecipientsData);
    const [reportRecipientPage, setReportRecipientPage] = useState(1);
    const [totalReportRecipientItems, setTotalReportRecipientItems] =
        useState(0);
    const [reportRecipientPageSize, setReportRecipientPageSize] = useState(
        reportRecipientTableStepOptions[0],
    );

    useEffect(() => {
        if (!school.attributes.isReportClient || !reportRecipientPage) return;

        const pagedReportRecipients = reportRecipientsData.slice(
            reportRecipientPage * reportRecipientPageSize -
                reportRecipientPageSize,
            reportRecipientPage * reportRecipientPageSize,
        );

        setReportRecipientPage(reportRecipientPage);
        setReportRecipients(pagedReportRecipients);
        setTotalReportRecipientItems(reportRecipientsData.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        reportRecipientPage,
        reportRecipientPageSize,
        school.attributes.reportRecipients,
    ]);

    const handleReportRecipientPageChange = ({
        page,
        startIndex,
        endIndex,
    }) => {
        const newPageSize = endIndex - startIndex;

        if (reportRecipientPageSize != newPageSize) {
            setReportRecipientPageSize(newPageSize);
            setReportRecipientPage(1);
            return;
        }
        setReportRecipientPage(page);
    };

    const reportRecipientDataColumns = [
        {
            header: 'Email',
            size: 'large',
            property: 'email',
            sortable: true,
        },
        {
            header: 'Action',
            size: 'small',
            property: 'id',
            primary: true,
            render: (recipient) => {
                const [buttonBusy, setButtonBusy] = useState();

                return (
                    <Button
                        pad={'none'}
                        color={'enrich-red'}
                        size="small"
                        plain
                        busy={buttonBusy}
                        label=<Trash
                            size="medium"
                            color="red"
                            onClick={async () => {
                                setButtonBusy(true);
                                try {
                                    const updated = reportRecipientsData
                                        .filter(
                                            (x) => x.email !== recipient.email,
                                        )
                                        .map((x) => x.email);

                                    await new SchoolService().update(school.id, {
                                        reportRecipients: updated,
                                    });

                                    setNotification({
                                        status: 'normal',
                                        title: 'Success',
                                        message: 'Report recipient removed.',
                                    });
                                    revalidator.revalidate();
                                } catch (error) {
                                    console.log(error);
                                    const message =
                                        error.response?.data?.error?.message ||
                                        error.message ||
                                        'Failed to remove report recipient.';
                                    setNotification({
                                        status: 'critical',
                                        title: 'Remove failed',
                                        message,
                                    });
                                }
                                setButtonBusy(false);
                            }}
                        />
                    />
                );
            },
            sortable: false,
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap={'medium'}>
                <Box
                    direction="row-responsive"
                    align="center"
                    fill="horizontal">
                    <Box
                        direction="row-responsive"
                        fill="horizontal"
                        gap={{ column: 'medium' }}>
                        <Box>
                            <Heading level="2">
                                {school.attributes.name}
                            </Heading>
                        </Box>
                        <Box alignSelf="center">
                            <Tag
                                background={{ color: 'enrich-blue' }}
                                name="Report Status "
                                value={
                                    school.attributes.isReportClient
                                        ? 'Active'
                                        : 'Inactive'
                                }
                            />
                        </Box>
                        {school.attributes.location?.data?.attributes?.name && (
                            <Box alignSelf="center">
                                <Tag
                                    background={{ color: 'enrich-green' }}
                                    name="Location "
                                    value={`${school.attributes.location.data.attributes.name}, ${school.attributes.location.data.attributes.state ?? '?'}`}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box
                        direction="row-responsive"
                        fill="horizontal"
                        justify="end"
                        gap={'medium'}>
                        <Box>
                            <EditSchoolButton school={school} />
                        </Box>
                        <Box>
                            <SchoolManualReportButton school={school} />
                        </Box>
                    </Box>
                </Box>

                <SchoolDashboardSection
                    schoolId={school.id}
                    admins={
                        school.attributes.schoolAdmins?.data?.map(
                            (a) => a.attributes,
                        ) || []
                    }
                />

                {school.attributes.isReportClient == true && (
                    <Box direction="row-responsive" fill="horizontal">
                        <Box basis="xlarge" fill="horizontal">
                            <Box
                                direction="row-responsive"
                                fill="horizontal"
                                pad={{ right: 'small' }}
                                gap="medium">
                                <Box basis="large" fill="horizontal">
                                    <Heading level="3">
                                        Report Recipients
                                    </Heading>
                                </Box>
                                <Box
                                    basis="small"
                                    justify="center"
                                    fill="horizontal">
                                    <AddSchoolReportRecipientButton
                                        schoolId={school.id}
                                        // eslint-disable-next-line max-len
                                        recipients={reportRecipientsData.map(
                                            (x) => x.email,
                                        )}
                                        setNotification={setNotification}
                                    />
                                </Box>
                            </Box>

                            <Box
                                direction="row-responsive"
                                elevation="large"
                                pad="medium"
                                fill="horizontal">
                                <DataTable
                                    sortable
                                    data={reportRecipients}
                                    columns={reportRecipientDataColumns}
                                    fill="horizontal"
                                />
                            </Box>

                            <Box
                                align="center"
                                justify="center"
                                margin={{ top: 'medium', bottom: 'none' }}>
                                <Pagination
                                    numberItems={totalReportRecipientItems}
                                    onChange={({
                                        page,
                                        startIndex,
                                        endIndex,
                                    }) => {
                                        handleReportRecipientPageChange({
                                            page,
                                            startIndex,
                                            endIndex,
                                        });
                                    }}
                                    size="small"
                                    step={reportRecipientPageSize}
                                    stepOptions={
                                        reportRecipientTableStepOptions
                                    }
                                    summary={true}
                                />
                            </Box>
                        </Box>
                        <Box basis="large" />
                    </Box>
                )}

            </PageContent>
            {notification && (
                <Notification
                    toast
                    status={notification.status}
                    title={notification.title}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}
        </Page>
    );
};

const SchoolDashboardSection = ({ schoolId, admins = [] }) => {
    const navigate = useNavigate();
    const [activeCount, setActiveCount] = useState(0);
    const [nonActiveCount, setNonActiveCount] = useState(0);
    const [alumniCount, setAlumniCount] = useState(0);
    const [recentEvents, setRecentEvents] = useState([]);

    useEffect(() => {
        let isActive = true;

        (async () => {
            const [active, nonActive, alumni, events] = await Promise.all([
                loadSchoolActiveCount(schoolId),
                loadSchoolNonActiveCount(schoolId),
                loadSchoolAlumniCount(schoolId),
                loadSchoolRecentEvents(schoolId),
            ]);

            if (isActive) {
                setActiveCount(active);
                setNonActiveCount(nonActive);
                setAlumniCount(alumni);
                setRecentEvents(events);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [schoolId]);

    const total = activeCount + nonActiveCount + alumniCount;

    return (
        <Box gap="medium">
            <Heading level="3" margin="none">
                School Dashboard
            </Heading>
            <Box direction="row" gap="medium" wrap>
                <SchoolStatCard
                    label="Active Students"
                    value={activeCount}
                    onClick={() => navigate(`/schools/${schoolId}/active`)}
                />
                <SchoolStatCard
                    label="Non-Active Students"
                    value={nonActiveCount}
                    onClick={() =>
                        navigate(`/schools/${schoolId}/inactive`)
                    }
                />
                <SchoolStatCard
                    label="Alumni"
                    value={alumniCount}
                    onClick={() => navigate(`/schools/${schoolId}/alumni`)}
                />
                <SchoolStatCard
                    label="Total Students"
                    value={total}
                    onClick={() => navigate(`/schools/${schoolId}/all`)}
                />
            </Box>
            {total > 0 && (
                <Box
                    gap="small"
                    pad="medium"
                    elevation="small"
                    round="small">
                    <Text weight="bold">Programme Status</Text>
                    <Meter
                        values={[
                            {
                                value: activeCount,
                                label: 'Active',
                                color: 'status-ok',
                            },
                            {
                                value: alumniCount,
                                label: 'Alumni',
                                color: 'accent-1',
                            },
                            {
                                value: nonActiveCount,
                                label: 'Non-Active',
                                color: 'status-warning',
                            },
                        ]}
                        max={total}
                        type="bar"
                        size="full"
                    />
                    <Box direction="row" justify="between">
                        <Text size="small">{activeCount} active</Text>
                        <Text size="small">{alumniCount} alumni</Text>
                        <Text size="small">
                            {nonActiveCount} non-active
                        </Text>
                    </Box>
                </Box>
            )}
            <Box fill="horizontal">
                <Heading level="3">Portal Admins</Heading>
                <Box elevation="medium" pad="medium">
                    <DataTable
                        sortable
                        data={admins}
                        columns={[
                            {
                                header: 'Name',
                                property: 'lastName',
                                render: (admin) =>
                                    `${admin.firstName || ''} ${admin.lastName || ''}`.trim() ||
                                    '—',
                                sortable: true,
                            },
                            {
                                header: 'Email',
                                property: 'email',
                                sortable: true,
                            },
                            {
                                header: 'Last Login',
                                property: 'lastLogin',
                                render: (admin) =>
                                    admin.lastLogin
                                        ? new Date(
                                              admin.lastLogin,
                                          ).toLocaleString()
                                        : 'Never',
                                sortable: true,
                            },
                        ]}
                    />
                </Box>
            </Box>
            {recentEvents.length > 0 && (
                <Box gap="small">
                    <Heading level="4" margin="none">
                        Recent Activity
                    </Heading>
                    <Box
                        pad="medium"
                        elevation="small"
                        round="small"
                        gap="small"
                        height={{ max: 'large' }}
                        overflow={{ vertical: 'auto' }}>
                        {recentEvents.map((event) => (
                            <Box
                                key={event.id}
                                direction="row"
                                gap="medium"
                                align="center">
                                <Tag
                                    value={new Date(
                                        event.createdAt,
                                    ).toLocaleDateString()}
                                    size="xsmall"
                                />
                                <Text size="small">
                                    <Anchor
                                        href={`/contacts/${event.contact.id}`}
                                        label={`${event.contact.firstName} ${event.contact.lastName}`}
                                    />{' '}
                                    {Utils.convertEnumStringToLabel(
                                        event.type,
                                    )}
                                </Text>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const SchoolStatCard = ({ label, value, onClick }) => (
    <Box
        pad="medium"
        elevation="small"
        round="small"
        align="center"
        gap="small"
        width="small"
        onClick={onClick}
        focusIndicator={false}
        style={{ cursor: 'pointer' }}>
        <Text size="xxlarge" weight="bold">
            {value}
        </Text>
        <Text size="small">{label}</Text>
    </Box>
);

const loadSchoolActiveCount = async (schoolId) => {
    const query = QueryString.stringify({
        filters: {
            enrichProgramStatus: 'active',
            school: schoolId,
        },
        pagination: { pageSize: 1 },
    });
    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadSchoolAlumniCount = async (schoolId) => {
    const query = QueryString.stringify({
        filters: {
            enrichProgramStatus: 'alumni',
            school: schoolId,
        },
        pagination: { pageSize: 1 },
    });
    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadSchoolNonActiveCount = async (schoolId) => {
    const query = QueryString.stringify({
        populate: {
            contacts: {
                filters: {
                    enrichProgramStatus: {
                        $in: AppConstants.nonActiveStudentStatuses,
                    },
                },
                fields: ['id'],
            },
        },
    });
    try {
        const response = await API.get(
            `/api/schools/${schoolId}?${query}`,
        );
        const schoolData = response.data.data;
        const flattened = Utils.deepFlatten(schoolData);
        return flattened.contacts?.length ?? 0;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadSchoolRecentEvents = async (schoolId) => {
    const gqlQuery = `
        query getRecentEvents($schoolId: ID!) {
            contacts(
                filters: { school: { id: { eq: $schoolId } } }
                pagination: { pageSize: 100 }
            ) {
                data {
                    id
                    attributes {
                        firstName
                        lastName
                        events(
                            filters: { type: { nei: "portal_login" } }
                            sort: ["createdAt:desc"]
                            pagination: { pageSize: 5 }
                        ) {
                            data {
                                id
                                attributes {
                                    type
                                    createdAt
                                }
                            }
                        }
                    }
                }
            }
        }
    `;
    try {
        const response = await API.post('graphql', {
            query: gqlQuery,
            variables: { schoolId },
        });
        const contacts = response.data.data?.contacts?.data ?? [];
        const events = contacts.flatMap((c) =>
            (c.attributes.events?.data ?? []).map((e) => ({
                id: e.id,
                type: e.attributes.type,
                createdAt: e.attributes.createdAt,
                contact: {
                    id: c.id,
                    firstName: c.attributes.firstName,
                    lastName: c.attributes.lastName,
                },
            })),
        );
        return events
            .sort(
                (a, b) =>
                    new Date(b.createdAt) - new Date(a.createdAt),
            )
            .slice(0, 20);
    } catch (error) {
        console.log(error);
        return [];
    }
};

export async function loader({ params }) {
    const query = QueryString.stringify({
        fields: ['id', 'name', 'isReportClient', 'reportRecipients'],
        populate: {
            schoolAdmins: {
                fields: ['firstName', 'lastName', 'email', 'lastLogin'],
            },
            location: {
                fields: ['name', 'state'],
            },
        },
    });

    try {
        const response = await API.get(
            `/api/schools/${params.schoolId}?${query}`,
        );
        return response.data.data;
    } catch (error) {
        console.log(error);
        return {};
    }
}
